import { useRouter } from 'next/router';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { useSettings } from '@/hooks/state/useSettings';
import { createTrackingSession, getPageType } from '@/utils/tracking';
import type { InitializeTrackingParams } from '@/utils/tracking/types';
interface TrackingProviderProps {
  children: React.ReactNode;
}
const TrackingContext = createContext({
  initializeTracking: (_params: InitializeTrackingParams) => {}
});
export const useTracking = () => {
  const ctx = useContext(TrackingContext);
  return ctx;
};
const sessionId = createTrackingSession();
export const TrackingProvider = ({
  children
}: TrackingProviderProps) => {
  const router = useRouter();
  const {
    settings
  } = useSettings();
  const user = settings?.user;
  const shouldIgnoreTracking = user?.hasAccountAdmin ?? user?.hasAccountArtist;
  const pathWithoutQuery = router.asPath.split('?')[0];
  const initializeTracking = useCallback(({
    content,
    access,
    endSlug,
    assetType,
    contentType
  }: InitializeTrackingParams) => {
    if (shouldIgnoreTracking) {
      return;
    }
    if (!sessionId) {
      return;
    }

    // If we don't have an asset type or content type, try to infer it from the slug.
    if (!(assetType && contentType) && endSlug) {
      const inferredData = getPageType(endSlug);

      // If we can't infer the asset type or content type, don't track.
      if (inferredData == null) {
        return;
      }
      assetType = inferredData.assetType;
      contentType = inferredData.contentType;
    }
    if (content === 'Playlist generator') {
      content = 'Playlist Generator';
    }
  }, [pathWithoutQuery, settings?.isBusiness, settings?.isPremium, settings?.user?.id, shouldIgnoreTracking]);
  return <TrackingContext.Provider value={useMemo(() => ({
    initializeTracking
  }), [initializeTracking])} data-sentry-element="TrackingContext.Provider" data-sentry-component="TrackingProvider" data-sentry-source-file="index.tsx">
      {children}
    </TrackingContext.Provider>;
};